import type { OffersSchemas } from "api-types";
import { forEach } from "lodash";

export class OfferTrancheFixture {
  private tranche: OffersSchemas.OfferTrancheDisplaySchema;

  constructor() {
    // Initialize with default values
    this.tranche = {
      startDate: "2023-10-18",
      advanceInLowestDenomination: 50_000,
      feeInLowestDenomination: 2_500,
      dailyCollectionFixedInLowestDenomination: null,
      dailyCollectionPercentage: 0.25,
      deploymentCurrency: "USD",
      deploymentMethod: "bank_transfer",
      collectionType: "percentage",
    };
  }

  withDeploymentDate(startDate: string): OfferTrancheFixture {
    this.tranche.startDate = startDate;
    return this;
  }

  withDeploymentCurrency(currency: string): OfferTrancheFixture {
    this.tranche.deploymentCurrency = currency;
    return this;
  }

  /**
   * Sets the advance amount for the tranche.
   *
   * NOTE: The amount should be specified in the currency's hightest denomination.
   */
  withAdvanceAmount(advanceAmount: number): OfferTrancheFixture {
    if (advanceAmount <= 1) {
      throw new Error("Advance amount must be greater than 1");
    }
    this.tranche.advanceInLowestDenomination = advanceAmount * 100;
    return this;
  }

  /**
   * Sets the fee as a percentage of the advance amount.
   *
   * NOTE: The amount should be specified as a number between 0 and 1.
   */
  withFeePercentage(percentage: number): OfferTrancheFixture {
    if (percentage < 0 || percentage > 1) {
      throw new Error("Fee percentage must be between 0 and 1");
    }
    this.tranche.feeInLowestDenomination = percentage * this.tranche.advanceInLowestDenomination;
    return this;
  }

  /**
   * Sets the percentage of daily sales collection for the tranche.
   *
   * NOTE: The amount should be specified as a number between 0 and 1.
   */
  asPercentageCollection(percentage: number): OfferTrancheFixture {
    if (percentage < 0 || percentage > 1) {
      throw new Error("Collection percentage must be between 0 and 1");
    }
    this.tranche.collectionType = "percentage";
    this.tranche.dailyCollectionPercentage = percentage * 100;
    this.tranche.dailyCollectionFixedInLowestDenomination = undefined;
    return this;
  }

  /**
   * Sets the daily fixed collection amount for the tranche.
   *
   * NOTE: The amount should be specified in the currency's hightest denomination.
   */
  asFixedCollection(dailyCollectionAmount: number): OfferTrancheFixture {
    if (dailyCollectionAmount <= 1) {
      throw new Error("Fixed collection amount must be greater than 1");
    }
    this.tranche.collectionType = "fixed";
    this.tranche.dailyCollectionFixedInLowestDenomination = dailyCollectionAmount * 100;
    this.tranche.dailyCollectionPercentage = undefined;
    return this;
  }

  build(): OffersSchemas.OfferTrancheDisplaySchema {
    // Validate that required fields are present
    if (this.tranche.collectionType === "percentage" && this.tranche.dailyCollectionPercentage === undefined) {
      throw new Error("Percentage collection type requires dailyCollectionPercentage");
    }

    if (
      this.tranche.collectionType === "fixed" &&
      this.tranche.dailyCollectionFixedInLowestDenomination === undefined
    ) {
      throw new Error("Fixed collection type requires dailyCollectionFixedInLowestDenomination");
    }

    return { ...this.tranche };
  }
}

export class OfferFixture {
  private offer: OffersSchemas.OfferDisplaySchema;

  constructor() {
    // Initialize with default values
    this.offer = {
      id: 1,
      topupAdvanceId: null,
      collectionCurrency: "USD",
      collectionFrequency: "daily",
      collectionMode: "blended",
      expirationDate: "",
      estimatedDurationDays: 180,
      tranches: [],
      capType: null,
      caps: [],
      aprDisclosureInfo: {
        estimatedApr: 30.0,
        estimatedMonthlyCostsInLowestDenomination: 1000,
        estimatedPaymentInLowestDenomination: 10000,
        paymentTerm: 100,
        estimatedTerm: 120,
        averageMonthlyRevenueInLowestDenomination: 1000,
        collectionFrequency: "daily",
        currency: "USD",
        estimatedMonthlyPaymentsInLowestDenomination: [1000],
      },
      isSelected: false,
      isTermLoan: false,
    };
  }

  withId(id: number): OfferFixture {
    this.offer.id = id;
    return this;
  }

  withCollectionCurrency(currency: string): OfferFixture {
    if (currency !== "USD") {
      // APR disclosure info is only required for USD
      this.offer.aprDisclosureInfo = null;
    }

    this.offer.collectionCurrency = currency;
    return this;
  }

  withCollectionFrequency(frequency: OffersSchemas.CollectionFrequency): OfferFixture {
    this.offer.collectionFrequency = frequency;
    return this;
  }

  withCollectionMode(mode: OffersSchemas.CollectionMode): OfferFixture {
    this.offer.collectionMode = mode;
    return this;
  }

  withEstimatedDurationDays(days: number): OfferFixture {
    if (days <= 1) {
      throw new Error("Estimated duration days must be greater than 1");
    }
    this.offer.estimatedDurationDays = days;
    return this;
  }

  withIsSelected(isSelected: boolean): OfferFixture {
    this.offer.isSelected = isSelected;
    return this;
  }

  withIsTermLoan(isTermLoan: boolean): OfferFixture {
    this.offer.isTermLoan = isTermLoan;
    return this;
  }

  withTranche(tranche: OffersSchemas.OfferTrancheDisplaySchema): OfferFixture {
    this.offer.tranches.push(tranche);
    return this;
  }

  withTopupAdvanceId(topupAdvanceId: number | null): OfferFixture {
    this.offer.topupAdvanceId = topupAdvanceId;
    return this;
  }

  /**
   * Sets uniform caps for the advance.
   *
   * NOTE: The amount should be specified in the currency's hightest denomination.
   */
  withUniformCaps(incrementAmount: number, period: 7 | 14 | 30): OfferFixture {
    this.offer.capType = "uniform";
    const totalRemittanceInLowestDenomination = this.offer.tranches.reduce(
      (acc, tranche) => acc + (tranche.advanceInLowestDenomination + tranche.feeInLowestDenomination),
      0
    );
    const incrementAmountInLowestDenomination = incrementAmount * 100;
    const numberOfCaps = Math.floor(totalRemittanceInLowestDenomination / incrementAmountInLowestDenomination);
    this.offer.caps = Array.from({ length: numberOfCaps }, (_, index) => ({
      id: index + 1,
      period: period,
      maxCollectionAmountInLowestDenomination: incrementAmountInLowestDenomination * (index + 1),
      differenceToPreviousCapInLowestDenomination: index === 0 ? null : incrementAmountInLowestDenomination,
    }));
    const remainderAmountInLowestDenomination =
      totalRemittanceInLowestDenomination % incrementAmountInLowestDenomination;
    if (remainderAmountInLowestDenomination > 0) {
      this.offer.caps.push({
        id: numberOfCaps + 1,
        period: period,
        maxCollectionAmountInLowestDenomination: totalRemittanceInLowestDenomination,
        differenceToPreviousCapInLowestDenomination: remainderAmountInLowestDenomination,
      });
    }

    return this;
  }

  /**
   * Sets variable caps for the advance.
   *
   * NOTE: The amounts should be specified in the currency's hightest denomination.
   */
  withVariableCaps(capAmounts: number[], period: 7 | 14 | 30): OfferFixture {
    const caps: OffersSchemas.OfferCapsDisplaySchema[] = [];

    forEach(capAmounts, (capAmount, index) => {
      if (capAmount <= 1) {
        throw new Error("Cap amount must be greater than 1");
      }

      if (index === 0) {
        caps.push({
          id: 1,
          period: period,
          maxCollectionAmountInLowestDenomination: capAmount * 100,
          differenceToPreviousCapInLowestDenomination: null,
        });
      } else {
        const differenceToPreviousCap = capAmount - capAmounts[index - 1];
        if (differenceToPreviousCap <= 0) {
          throw new Error("Cap amount must be greater than the previous cap amount");
        }

        caps.push({
          id: index + 1,
          period: period + index * period,
          maxCollectionAmountInLowestDenomination: capAmount * 100,
          differenceToPreviousCapInLowestDenomination: differenceToPreviousCap * 100,
        });
      }
    });

    this.offer.capType = "variable";
    this.offer.caps = caps;

    return this;
  }

  withAprDisclosureInfo(options: Partial<OffersSchemas.OfferAprDisclosureInfoSchema> | null): OfferFixture {
    if (options === null) {
      this.offer.aprDisclosureInfo = null;
    } else {
      this.offer.aprDisclosureInfo = {
        ...(this.offer.aprDisclosureInfo ?? {
          averageMonthlyRevenueInLowestDenomination: 2_000_00,
          collectionFrequency: this.offer.collectionFrequency,
          currency: this.offer.collectionCurrency,
          estimatedApr: 0.25,
          estimatedMonthlyCostsInLowestDenomination: 1_000_00,
          estimatedMonthlyPaymentsInLowestDenomination: [1_000],
          estimatedPaymentInLowestDenomination: 1_000_00,
          estimatedTerm: 30,
          paymentTerm: 30,
        }),
        ...options,
      };
    }
    return this;
  }

  build(): OffersSchemas.OfferDisplaySchema {
    if (this.offer.tranches.length === 0) {
      throw new Error("Offer must have at least one tranche");
    }

    return {
      ...this.offer,
      tranches: this.offer.tranches.map((tranche) => ({ ...tranche })),
      caps: this.offer.caps.map((cap) => ({ ...cap })),
      aprDisclosureInfo: this.offer.aprDisclosureInfo
        ? {
            ...this.offer.aprDisclosureInfo,
            estimatedMonthlyPaymentsInLowestDenomination: [
              ...(this.offer.aprDisclosureInfo.estimatedMonthlyPaymentsInLowestDenomination || []),
            ],
          }
        : null,
    };
  }
}
